import React from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { DefaultSwitch as Switch } from 'services/UIUtils';
import { useElementMetadata, toMetadataNumber } from './useElementMetadata';

// The runtime reads `metadata.hst ?? fee_hst`, so an absent key inherits the
// document-wide default while an explicit false opts the line out of it. The
// select exposes all three states rather than collapsing them into a switch.
const HST_INHERIT = 'HST: document default';
const HST_ALWAYS = 'HST: always charge';
const HST_NEVER = 'HST: never charge';
const HST_OPTIONS = [HST_INHERIT, HST_ALWAYS, HST_NEVER];

const hstToOption = (hst) => {
  if (hst === true) return HST_ALWAYS;
  if (hst === false) return HST_NEVER;
  return HST_INHERIT;
};

const optionToHst = (option) => {
  if (option === HST_ALWAYS) return true;
  if (option === HST_NEVER) return false;
  return undefined;
};

const MalformedMetadataNote = () => (
  <div className="pricing-fields-warning" style={{ fontSize: '12px', margin: '8px 0' }}>
    This element&apos;s metadata isn&apos;t valid JSON, so the pricing helper is turned off. Fix it
    in the Metadata box below and the helper will come back.
  </div>
);

/**
 * Pricing controls for a chargeable checkbox. Writes `fee`, `description` and
 * `hst`; leaves every other metadata key alone.
 */
export const CheckboxPricingFields = ({ element, updateField }) => {
  const { metadata, malformed, patch } = useElementMetadata(element, updateField);

  if (malformed) return <MalformedMetadataNote />;

  // Presence of the key, not its validity, decides the toggle — otherwise
  // clearing the fee box mid-edit would collapse the whole section.
  const chargeable = metadata.fee !== undefined;

  return (
    <>
      <Switch
        label="Chargeable"
        checked={chargeable}
        onChange={(event, isInputChecked) =>
          patch(
            isInputChecked
              ? { fee: '' }
              : { fee: undefined, description: undefined, hst: undefined },
          )
        }
      />
      {chargeable && (
        <>
          <DefaultTextField
            label="Fee ($)"
            name="fee"
            value={metadata.fee ?? ''}
            onChange={(event) => patch({ fee: toMetadataNumber(event.target.value) ?? '' })}
          />
          <DefaultTextField
            label="Charge description"
            name="fee_description"
            placeholder={element.id}
            value={metadata.description ?? ''}
            onChange={(event) => patch({ description: event.target.value || undefined })}
          />
          <DefaultSelectField
            label="HST"
            width="250px"
            value={hstToOption(metadata.hst)}
            onChange={(event) => patch({ hst: optionToHst(event.target.value) })}
            inputs={HST_OPTIONS}
          />
        </>
      )}
    </>
  );
};

/**
 * Pricing controls for the `fees` smart input itself: a processing fee added to
 * every charge, and the document-wide HST default the checkboxes inherit.
 */
export const FeeFieldPricingFields = ({ element, updateField }) => {
  const { metadata, malformed, patch } = useElementMetadata(element, updateField);

  if (malformed) return <MalformedMetadataNote />;

  return (
    <>
      <DefaultTextField
        label="Processing fee ($)"
        name="fixed_fee"
        placeholder="None"
        value={metadata.fixed_fee ?? ''}
        onChange={(event) => patch({ fixed_fee: toMetadataNumber(event.target.value) })}
      />
      <Switch
        label="Charge HST by default"
        checked={!!metadata.hst}
        onChange={(event, isInputChecked) => patch({ hst: isInputChecked || undefined })}
      />
    </>
  );
};

export default CheckboxPricingFields;
